import React, { useCallback } from 'react';
import { View, Pressable, StyleSheet } from 'react-native';
import { useRouter } from 'expo-router';
import { useExploreStore, useExploreLightboxStore, useChatInputStore, useToastStore } from '@/store';
import { trackEvent } from '@/constants/utils/analytics';
import copyText from '@/constants/utils/copy_text';
import Icon from '@/components/Icon';
import Text from '@/components/Typography/Text';
import useRequireAuth from '@/hooks/useRequireAuth';

const ExploreCardQuickActions = React.memo(({ item }) => {
  const router = useRouter();
  const cloneItem = useExploreStore(state => state.actions.cloneItem);
  const openExploreLightbox = useExploreLightboxStore(state => state.actions.openExploreLightbox);
  const addToast = useToastStore(state => state.actions.addToast);
  const requireAuth = useRequireAuth();

  // Anonymous gating helper for committing quick actions (Clone).
  // Returns true when the action should proceed.
  const gateGenerate = useCallback((analyticsKey) => {
    const ran = requireAuth(() => true, {
      source: `explore_${analyticsKey}`,
      title: 'Sign in to create with this prompt',
      subtitle: 'Free accounts unlock remixing prompts and generating new images, video, music, and 3D.',
      icon: 'wand-magic-sparkles',
    });
    if (ran !== true) {
      trackEvent('explore', 'explore_auth_required', analyticsKey);
      return false;
    }
    return true;
  }, [requireAuth]);

  const handleUsePrompt = useCallback(async (e) => {
    e.stopPropagation();
    if (!gateGenerate('quick_action_clone')) return;
    trackEvent('explore', 'explore_clone', item.slug);
    try {
      const clone = await cloneItem(item.slug);
      if (!clone) return;
      const store = useChatInputStore.getState();
      const prompt = clone.prompt || item.prompt;
      // The backend returns BOTH the slug (clone.modelSlug) and the resolved
      // Model document (clone.model). The chat pipeline expects an ObjectId
      // in `modelIds[]`; passing a slug like "imagen-4-0" makes the
      // /conversations/new endpoint blow up trying to $in-cast it.
      const modelId = clone.model?.id || null;
      if (item.mediaType === 'video') {
        store.initializeVideoGeneration(prompt, modelId, true);
      } else if (item.mediaType === 'music') {
        store.initializeMusicGeneration(prompt, modelId, true, clone.musicContext?.description || null);
      } else {
        store.initializeImageGeneration(prompt, modelId, true);
      }
      router.push('/app');
    } catch {
      addToast('Clone failed', 'error');
    }
  }, [item, cloneItem, router, gateGenerate, addToast]);

  // Open the item detail with video output preselected. Do NOT mint/auto-send
  // here — video generation is expensive and users often just want to read the
  // prompt. Generation requires an explicit confirm in the lightbox.
  const handleMakeVideo = useCallback((e) => {
    e.stopPropagation();
    trackEvent('explore', 'explore_make_video', item.slug);
    openExploreLightbox(item, useExploreStore.getState().items, { initialMediaType: 'video' });
  }, [item, openExploreLightbox]);

  const handleCopy = useCallback(async (e) => {
    e.stopPropagation();
    if (!item?.prompt) return;
    trackEvent('explore', 'explore_copy_prompt', item.slug);
    try {
      await copyText(item.prompt);
      addToast('Prompt copied ✓', 'success');
    } catch {
      addToast('Failed to copy', 'error');
    }
  }, [item, addToast]);

  const handleShare = useCallback(async (e) => {
    e.stopPropagation();
    trackEvent('explore', 'explore_share', item?.slug);
    try {
      await copyText(`https://gab.ai/explore/item/${item?.slug}`);
      addToast('Link copied', 'success');
    } catch {
      addToast('Failed to copy link', 'error');
    }
  }, [item, addToast]);

  return (
    <View style={styles.container} pointerEvents="box-none">
      <ActionButton icon="sparkles" onPress={handleUsePrompt} title='Clone' showTitle={true} />
      {item.mediaType === 'image' && (
        <ActionButton icon="video" onPress={handleMakeVideo} title='Make Video' showTitle={true} />
      )}
      <ActionButton icon="copy" onPress={handleCopy} title='Copy Prompt' />
      {/* <ActionButton icon="share-nodes" onPress={handleShare} title='Share' /> */}
    </View>
  );
});

const ActionButton = React.memo(({ icon, onPress, title, showTitle=false }) => (
  <Pressable
    onPress={onPress}
    title={title}
    style={({ hovered }) => [
      styles.button,
      hovered && styles.buttonHovered,
      showTitle && styles.buttonWithTitle,
    ]}
  >
    <Icon name={icon} size={12} color="#fff" />
    {showTitle && <Text size="s" color="white">{title}</Text>}
  </Pressable>
));

const styles = StyleSheet.create({
  container: {
    position: 'absolute',
    top: 6,
    right: 6,
    flexDirection: 'row',
    gap: 4,
  },
  button: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 4,
    width: 28,
    height: 28,
    borderRadius: 14,
    backgroundColor: 'rgba(0,0,0,0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    backdropFilter: 'blur(8px)',
  },
  buttonWithTitle: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 4,
    width: 'auto',
    paddingHorizontal: 10,
  },
  buttonHovered: {
    backgroundColor: 'rgba(0,0,0,0.75)',
  },
});

ExploreCardQuickActions.displayName = 'ExploreCardQuickActions';
ActionButton.displayName = 'ActionButton';

export default ExploreCardQuickActions;
